import React, { useState, useRef, useCallback } from "react";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { cn } from "../lib/utils.js";
import { TagView } from "./TagView.js";
import { TagEditor } from "./TagEditor.js";
import type { TagOptions } from "../lib/api.js";
import { ImportToDriveModal } from "./ImportToDriveModal.js";
import { CollectionPicker } from "./CollectionPicker.js";
import { EditPhotoLinkPanel } from "./EditPhotoLinkPanel.js";

interface InfoPanelProps {
  photo: PhotoRecord;
  currentUser?: string | null;
  /** Whether the current session has admin role. */
  isAdmin?: boolean;
  isReadOnly?: boolean;
  isEditMode: boolean;
  allPhotos: PhotoRecord[];
  tagOptions?: TagOptions;
  onToggleEditMode: () => void;
  onToggleLiked: () => void;
  onOpenGallery: () => void;
  onSavedTags: (patch: Partial<PhotoRecord>) => void;
  onAdminDriveLinkSaved?: (link: string) => void;
  /** Admin replaced the photographer's link (wrong set) — patch local state. */
  onPhotoLinkSaved?: (patch: Partial<PhotoRecord>) => void;
  onRename?: (newName: string) => void;
}

/** Expected number of preview thumbnails per record. Records with fewer
 *  than this trigger an admin-visible re-sync banner. */
const EXPECTED_PREVIEW_COUNT = 5;

export function InfoPanel({
  photo,
  currentUser,
  isAdmin,
  isReadOnly,
  isEditMode,
  allPhotos,
  tagOptions,
  onToggleEditMode,
  onToggleLiked,
  onOpenGallery,
  onSavedTags,
  onAdminDriveLinkSaved,
  onPhotoLinkSaved,
  onRename,
}: InfoPanelProps) {
  const isLiked =
    currentUser && !isAdmin
      ? photo.likedBy.includes(currentUser)
      : photo.liked;
  const hasDriveLink = Boolean(photo.driveLink?.trim());
  const hasAdminLink = Boolean(photo.adminDriveLink?.trim());
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showEditLink, setShowEditLink] = useState(false);
  const [linkSaved, setLinkSaved] = useState(false);

  // ── Preview re-sync (admin only when fewer than expected previews) ──
  const previewThumbCount = photo.previewURLs?.thumbs?.length ?? 0;
  const needsResync = isAdmin && previewThumbCount < EXPECTED_PREVIEW_COUNT;
  const [resyncState, setResyncState] = useState<"idle" | "queuing" | "queued" | "error">("idle");
  const handleResync = useCallback(async () => {
    if (resyncState === "queuing" || resyncState === "queued") return;
    setResyncState("queuing");
    try {
      const { requeuePreviewSync } = await import("../lib/api.js");
      await requeuePreviewSync(photo.id);
      setResyncState("queued");
    } catch (err) {
      console.error("[resync] failed:", err);
      setResyncState("error");
    }
  }, [photo.id, resyncState]);

  return (
    <>
    <aside
      className={cn(
        "flex h-full min-h-0 flex-col border-t border-surface-2 bg-[#fbfaf6]",
        "md:basis-[40%] md:border-l md:border-t-0",
        "shadow-[-10px_0_40px_rgba(44,38,29,0.04)]"
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-surface-2 px-5 pb-4 pt-5 md:px-7 md:pb-5 md:pt-6">
        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
            Review
          </p>
          {photo.photographer === "Admin" && onRename ? (
            <InlineRenameHeading value={photo.chatName} onSave={onRename} />
          ) : (
            <h2 className="text-xl font-semibold leading-tight text-ink-0 md:text-[26px]">
              {photo.chatName}
            </h2>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-2">
            {photo.photographer ? <span>{photo.photographer}</span> : <span>Chưa rõ thợ ảnh</span>}
          </div>
        </div>

        {isAdmin && (
          <div className="flex-shrink-0 text-xs font-medium text-ink-3">
            {isEditMode ? "Đang chỉnh tags" : null}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 md:px-7 md:py-6">
        {/* ── Action buttons ────────────────────────────────────────── */}
        <div className="mb-6 grid grid-cols-2 gap-2">
          {!isReadOnly ? (
            <>
              <ActionButton active={isLiked} onClick={onToggleLiked}>
                <HeartIcon filled={isLiked} />
                <span>{isLiked ? "Liked" : "Like"}</span>
              </ActionButton>
              {isAdmin ? (
                <button
                  onClick={() => setShowImportModal(true)}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-[18px] border px-4 py-3 text-sm font-medium transition-colors",
                    hasAdminLink
                      ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                      : "border-surface-2 bg-white/84 text-ink-1 hover:bg-surface-1 hover:text-ink-0"
                  )}
                >
                  <ImportIcon />
                  <span>{hasAdminLink ? "Imported ✓" : "Import Drive"}</span>
                </button>
              ) : (
                <div />
              )}
            </>
          ) : (
            <div className="col-span-2 rounded-[22px] border border-surface-2 bg-white/70 px-4 py-3 text-sm text-ink-2">
              Chế độ xem nhanh. Các thao tác chỉnh sửa đang được ẩn.
            </div>
          )}
          <button
            onClick={onOpenGallery}
            className="flex items-center justify-center gap-2 rounded-[18px] border border-surface-2 bg-[#f3efe6] px-4 py-3 text-sm font-semibold text-ink-1 transition-colors hover:bg-[#ece6da]"
          >
            <GridIcon />
            Tất cả ảnh
          </button>
          {hasDriveLink ? (
            <a
              href={photo.driveLink.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-[18px] border border-surface-2 bg-white px-4 py-3 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-1 hover:text-ink-0"
            >
              <DriveIcon />
              Google Drive
            </a>
          ) : (
            <div className="flex items-center justify-center gap-2 rounded-[18px] border border-surface-2 bg-surface-1 px-4 py-3 text-sm font-medium text-ink-3">
              <DriveIcon />
              Không có link
            </div>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowCollectionPicker(true)}
              className="col-span-2 flex items-center justify-center gap-2 rounded-[18px] border border-surface-2 bg-white/84 px-4 py-3 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-1 hover:text-ink-0"
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <path d="M17.5 14v7M14 17.5h7" />
              </svg>
              Thêm vào Collection
            </button>
          )}
        </div>

        {/* ── Fix the photographer's link (admin) ───────────────────────── */}
        {isAdmin && !isReadOnly && !showEditLink && (
          <div className="-mt-3 mb-6 flex justify-end">
            <button
              onClick={() => {
                setShowEditLink(true);
                setLinkSaved(false);
              }}
              className="text-[12px] font-medium text-ink-3 underline-offset-2 transition-colors hover:text-[#B76E6E] hover:underline"
            >
              Thợ gửi nhầm link? Sửa link ảnh
            </button>
          </div>
        )}
        {linkSaved && !showEditLink && (
          <div className="mb-6 rounded-[18px] border border-green-200 bg-green-50 px-4 py-3 text-[13px] text-green-800">
            ✓ Đã lưu link mới. Ảnh xem trước tự cập nhật sau ~1 phút (tải lại trang để thấy).
          </div>
        )}
        {showEditLink && (
          <EditPhotoLinkPanel
            photo={photo}
            onClose={() => setShowEditLink(false)}
            onSaved={(patch) => {
              onPhotoLinkSaved?.(patch);
              setShowEditLink(false);
              setLinkSaved(true);
            }}
          />
        )}

        {/* ── Re-sync preview banner (admin, when previews incomplete) ── */}
        {needsResync && (
          <div className="mb-6 rounded-[20px] border border-amber-200 bg-amber-50/80 px-4 py-3.5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4M12 17h.01" />
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-amber-900">
                  Chỉ có {previewThumbCount}/{EXPECTED_PREVIEW_COUNT} ảnh preview
                </p>
                <p className="mt-0.5 text-[12px] text-amber-800/90">
                  Có thể khách upload thêm ảnh sau lần sync trước. Bấm để queue lại — cron sẽ chạy trong ~1 phút.
                </p>
                <button
                  onClick={handleResync}
                  disabled={resyncState === "queuing" || resyncState === "queued"}
                  className={cn(
                    "mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors",
                    resyncState === "queued"
                      ? "bg-green-100 text-green-800"
                      : resyncState === "error"
                        ? "bg-red-100 text-red-800 hover:bg-red-200"
                        : "bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                  )}
                >
                  {resyncState === "queuing" && (
                    <>
                      <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                        <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                        <path d="M21 12a9 9 0 00-9-9" strokeLinecap="round" />
                      </svg>
                      Đang queue…
                    </>
                  )}
                  {resyncState === "queued" && <>✓ Đã queue, chờ ~1 phút</>}
                  {resyncState === "error" && <>Lỗi — thử lại</>}
                  {resyncState === "idle" && <>Re-sync preview</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Admin Drive link (when imported) ────────────────────────── */}
        {isAdmin && hasAdminLink && (
          <div className="mb-6">
            <a
              href={photo.adminDriveLink.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-[18px] border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 transition-colors hover:bg-green-100"
            >
              <DriveIcon />
              Mở Admin Drive
            </a>
          </div>
        )}

        {/* Hide metadata for legacy imported photos (no dates available) */}
        {photo.photographer !== "Admin" && (
          <section className="mb-6 rounded-[24px] border border-surface-2 bg-white/70 px-4 py-4 shadow-[inset_0_0_0_1px_rgba(26,25,23,0.02)] md:px-5">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
              Metadata
            </p>
            <div className="space-y-3.5">
              <MetaRow label="Ngày chụp" value={formatDateShort(photo.shootDate)} />
              <MetaRow label="Ngày chọn ảnh" value={formatDateShort(photo.photoSelectionDate)} />
              <MetaRow label="Ngày trả ảnh" value={formatDateShort(photo.returnDate)} />
              <MetaRow label="Thời gian xử lý" value={calcProcessingDays(photo.photoSelectionDate, photo.returnDate)} />
            </div>
          </section>
        )}

        <section className="mb-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
              {isEditMode ? "Edit tags" : "Tags"}
            </p>
            {isAdmin && (
              <button
                onClick={onToggleEditMode}
                className="text-xs font-medium text-ink-2 transition-colors hover:text-ink-0"
              >
                {isEditMode ? "Xem tags" : "Chỉnh sửa"}
              </button>
            )}
          </div>

          {isEditMode && isAdmin ? (
            <TagEditor photo={photo} allPhotos={allPhotos} tagOptions={tagOptions} onSaved={onSavedTags} />
          ) : (
            <TagView photo={photo} />
          )}
        </section>

      </div>
    </aside>

    {/* Import to Drive modal */}
    {showImportModal && (
      <ImportToDriveModal
        photo={photo}
        onClose={() => setShowImportModal(false)}
        onSaved={(link) => {
          onAdminDriveLinkSaved?.(link);
          setShowImportModal(false);
        }}
      />
    )}

    {/* Collection picker */}
    {showCollectionPicker && (
      <CollectionPicker
        photoIds={[photo.id]}
        onClose={() => setShowCollectionPicker(false)}
      />
    )}
    </>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink-0">{value}</span>
    </div>
  );
}

function ActionButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-[18px] border px-4 py-3 text-sm font-medium transition-colors",
        active
          ? "border-[#d8ccbb] bg-[#efe5d7] text-ink-0"
          : "border-surface-2 bg-white/84 text-ink-2 hover:bg-surface-1 hover:text-ink-0"
      )}
    >
      {children}
    </button>
  );
}

function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function calcProcessingDays(selectionDate: string | null | undefined, returnDate: string | null | undefined): string {
  if (!selectionDate || !returnDate) return "—";
  try {
    const diff = new Date(returnDate).getTime() - new Date(selectionDate).getTime();
    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return "—";
    if (days === 0) return "Cùng ngày";
    return `${days} ngày`;
  } catch {
    return "—";
  }
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={2.3}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function DriveIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.5 21L9 13.5H1.5L9 2.25h6L10.5 9H18L13.5 21H4.5z" opacity={0.62} />
      <path d="M10.5 9H18L22.5 21H13.5L10.5 9z" opacity={0.82} />
      <path d="M9 2.25h6L19.5 9H13.5L9 2.25z" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v13M7 11l5 5 5-5" />
      <path d="M20 21H4" />
    </svg>
  );
}

function InlineRenameHeading({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback(() => {
    setDraft(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [value]);

  const save = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        className="text-xl font-semibold leading-tight text-ink-0 md:text-[26px] bg-transparent border-b-2 border-[#B76E6E] outline-none w-full"
      />
    );
  }

  return (
    <h2
      className="text-xl font-semibold leading-tight text-ink-0 md:text-[26px] group/rename cursor-text flex items-start gap-2"
      onClick={startEdit}
    >
      <span>{value}</span>
      <button
        onClick={startEdit}
        className="flex-shrink-0 mt-1 p-1 rounded-lg text-ink-3 opacity-0 group-hover/rename:opacity-100 hover:text-[#B76E6E] hover:bg-[#B76E6E]/10 transition-all"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
    </h2>
  );
}
